import { animate, style, transition, trigger } from '@angular/animations';

export enum ModalPresetIdentifier {
  NONE = '',
  ZOOMANDFADEIN = 'zoomAndFadeIn',
  SLIDEINFROMLEFT = 'slideInFromLeft',
  SLIDEINFROMRIGHT = 'slideInFromRight',
  SLIDEINFROMBOTTOM = 'slideInFromBottom',
  SLIDEINFROMTOP = 'slideInFromTop',
  SLIDEANDFADEUP = 'slideAndFadeUp',
  FADEINANDSCALE = 'fadeInAndScale',
  POPIN = 'popIn',
  SPININ = 'spinIn',
  BOUNCEIN = 'bounceIn',
}

type ModalTransitionPreset = {
  enterTransform: string;
  enterOpacity: number;
  enterBorderRadius: string;
  enterRotate?: string;

  leaveTransform: string;
  leaveOpacity: number;
  leaveBorderRadius: string;
  leaveRotate?: string;

  duration: number;
  easing: string;
};

export const modalTransition = trigger('modalTransition', [
  // Transition for opening (void state to 'in') - use void instead of '*' to avoid "doubling" animation on modal open
  transition('void => in', [
    style({
      opacity: '{{enterOpacity}}',
      transform: '{{enterTransform}}',
      borderRadius: '{{enterBorderRadius}}',
    }),
    animate(
      '{{duration}}ms {{easing}}',
      style({
        opacity: '{{leaveOpacity}}',
        transform: '{{leaveTransform}}',
        borderRadius: '{{leaveBorderRadius}}',
      }),
    ),
  ]),

  // from modal to modal do nothing
  transition('in => active', []),

  // Reverse transition for closing ('in' to any)
  transition('in => *', [
    style({
      opacity: '{{leaveOpacity}}',
      transform: '{{leaveTransform}}',
      borderRadius: '{{leaveBorderRadius}}',
    }),
    animate(
      '{{duration}}ms {{easing}}',
      style({
        opacity: '{{enterOpacity}}',
        transform: '{{enterTransform}}',
        borderRadius: '{{enterBorderRadius}}',
      }),
    ),
  ]),
]);

export const modalTransitionPresets: Record<string, ModalTransitionPreset> = {
  [ModalPresetIdentifier.NONE]: {
    enterTransform: 'translateX(0) translateY(0) scale(1)',
    leaveTransform: 'translateX(0) translateY(0) scale(1)',
    enterOpacity: 1,
    leaveOpacity: 1,
    enterBorderRadius: '0%',
    leaveBorderRadius: '0%',
    duration: 0,
    easing: 'linear',
  },
  [ModalPresetIdentifier.ZOOMANDFADEIN]: {
    enterTransform: 'translateX(100%) translateY(0) scale(0.8)',
    leaveTransform: 'translateX(0) translateY(0) scale(1)',
    enterOpacity: 0,
    leaveOpacity: 1,
    enterBorderRadius: '50%',
    leaveBorderRadius: '0%',
    duration: 500,
    easing: 'ease-in-out',
  },
  [ModalPresetIdentifier.SLIDEINFROMLEFT]: {
    enterTransform: 'translateX(-100%) translateY(0) scale(1)',
    leaveTransform: 'translateX(0) translateY(0) scale(1)',
    enterOpacity: 1,
    leaveOpacity: 1,
    enterBorderRadius: '0%',
    leaveBorderRadius: '0%',
    duration: 300,
    easing: 'ease-out',
  },
  [ModalPresetIdentifier.SLIDEINFROMRIGHT]: {
    enterTransform: 'translateX(100%) translateY(0) scale(1)',
    leaveTransform: 'translateX(0) translateY(0) scale(1)',
    enterOpacity: 1,
    leaveOpacity: 1,
    enterBorderRadius: '0%',
    leaveBorderRadius: '0%',
    duration: 300,
    easing: 'ease-out',
  },
  [ModalPresetIdentifier.SLIDEINFROMBOTTOM]: {
    enterTransform: 'translateX(0) translateY(100%) scale(1)',
    leaveTransform: 'translateX(0) translateY(0) scale(1)',
    enterOpacity: 1,
    leaveOpacity: 1,
    enterBorderRadius: '0%',
    leaveBorderRadius: '0%',
    duration: 300,
    easing: 'ease-out',
  },
  [ModalPresetIdentifier.SLIDEINFROMTOP]: {
    enterTransform: 'translateX(0) translateY(-100%) scale(1)',
    leaveTransform: 'translateX(0) translateY(0) scale(1)',
    enterOpacity: 1,
    leaveOpacity: 1,
    enterBorderRadius: '0%',
    leaveBorderRadius: '0%',
    duration: 300,
    easing: 'ease-out',
  },
  [ModalPresetIdentifier.SLIDEANDFADEUP]: {
    enterTransform: 'translateX(0) translateY(100%) scale(1)',
    leaveTransform: 'translateX(0) translateY(0) scale(1)',
    enterOpacity: 0,
    leaveOpacity: 1,
    enterBorderRadius: '0%',
    leaveBorderRadius: '0%',
    duration: 400,
    easing: 'ease-in-out',
  },
  [ModalPresetIdentifier.FADEINANDSCALE]: {
    enterTransform: 'translateX(0) translateY(0) scale(0.9)',
    leaveTransform: 'translateX(0) translateY(0) scale(1)',
    enterOpacity: 0,
    leaveOpacity: 1,
    enterBorderRadius: '0%',
    leaveBorderRadius: '0%',
    duration: 400,
    easing: 'ease-in',
  },
  [ModalPresetIdentifier.POPIN]: {
    enterTransform: 'translateX(0) translateY(0) scale(0.5)',
    leaveTransform: 'translateX(0) translateY(0) scale(1)',
    enterOpacity: 0,
    leaveOpacity: 1,
    enterBorderRadius: '0%',
    leaveBorderRadius: '0%',
    duration: 300,
    easing: 'ease-out',
  },
  [ModalPresetIdentifier.SPININ]: {
    enterTransform: 'translateX(0) translateY(0) scale(1) rotate(-360deg)',
    leaveTransform: 'translateX(0) translateY(0) scale(1) rotate(0deg)',
    enterOpacity: 0,
    leaveOpacity: 1,
    enterBorderRadius: '0%',
    leaveBorderRadius: '0%',
    duration: 500,
    easing: 'ease-in-out',
  },
  [ModalPresetIdentifier.BOUNCEIN]: {
    enterTransform: 'translateX(0) translateY(-50%) scale(1.2)',
    leaveTransform: 'translateX(0) translateY(0) scale(1)',
    enterOpacity: 0,
    leaveOpacity: 1,
    enterBorderRadius: '0%',
    leaveBorderRadius: '0%',
    duration: 500,
    easing: 'cubic-bezier(0.68, -0.55, 0.27, 1.55)',
  },
};
